import { AgGridReact } from 'ag-grid-react'
import { agConfigSideBar, tableCellStyle } from '../../../../utils/AgGridConfig'
import { memo, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { CellClickedEvent, ColDef } from 'ag-grid-community'
import {
  AmountInvestedRender,
  MoicRender,
  UnrealizedValueRender,
  CompanyNameRender,
  FundRender,
  InvestedRoundRender,
  IrrRender,
  RoundRender,
  FdbPercentageRender,
  FdbSharesRender,
  TagsRender,
  UnrealizedCostRender,
  RealizedValueRender,
  JurisdictionRender,
  ActionRender,
  EsopHoldingRender,
  ValuationRender,
} from './PortfolioAGComponent'
import {
  AllTransactionsType,
  CompanyObject,
  GetAllPrivateCaptablesQuery,
  StageCompanyType,
  Transaction_Type,
  useGetAllPrivateCaptablesQuery,
} from '../../../../generated/graphql'
import { RateInterval, convertRate, xirr } from 'node-irr'
import { calculateXirr } from '../../../../utils/Utils'
import { KTSVG } from '../../../../_metronic/helpers'
import { GlobalContext } from '../../store/context/globalContext'
import { getCurrency, getSymbolOfCurrency } from '../../../../utils/CurrencyFormatter'
import { MenuComponent } from '../../../../_metronic/assets/ts/components'
import { AddIndirectTransactionModal } from '../../manual-investments/components/AddAdditionalIndirectTransaction/AddIndirectTransactionModal'
import { AddDirectTransactionModal } from '../../manual-investments/components/AddAdditionalDirectTransaction/AddDirectTransactionModal'
import { InvestorListType } from '../../company/views/HissaCompanySummaryPage'
import { AddTransactionPopup } from './AddTransactionPopUp'
import { UploadCaptablePopup } from '../../my-captables/components/UploadCaptablePopup'
import { CaptableFileUploadPopup } from '../../my-captables/components/CaptableFileUploadPopup'
import { graphqlRequestClient } from '../../../../queries/client'
import CloudSetting from '../../../../utils/tableStateIcon'
import { TableId } from '../../../../utils/TableIdConstants'
import { useTableStateManagement } from '../../../../utils/TableHook'
import _ from 'lodash'
import { Grid2X2Plus, Plus } from 'lucide-react'

type PortfolioTableProps = {
  allTransaction?: AllTransactionsType[] //need to change types
  investors: InvestorListType[]
  allPrivateCompanies: string[]
}

const toFiniteNumber = (value: unknown): number => {
  const parsedValue = Number(value)
  return Number.isFinite(parsedValue) ? parsedValue : 0
}

export function PortfolioAGTable(props: PortfolioTableProps) {
  const gridApi = useRef<any>(null)
  const companySearchTerm = useRef('')
  const { state } = useContext(GlobalContext)
  const { userCurrency } = state
  const userCurrencyFormat = getCurrency(userCurrency)
  const allTransaction = useMemo(
    () =>
      props.allTransaction
        ? [...props.allTransaction].sort((a, b) =>
            a.companyData.name.localeCompare(b.companyData.name)
          )
        : props.allTransaction,
    [props.allTransaction]
  )

  const [pageLoaded, setPageLoaded] = useState(false)
  const { filterCondition, setFilterCondition, isSaving, uploadFilter, uploadColumn, tableState } =
    useTableStateManagement(gridApi, TableId.portfolioTable + 'web', pageLoaded)

  const [showCaptableModal, setShowCaptableModal] = useState(false)
  const [stageType, setStageType] = useState<string>('stage_4')
  const [isCaptableModalOpen, setIsCaptableModalOpen] = useState<boolean>(false)
  const [isFileModal, setIsFileModal] = useState<boolean>(false)
  useEffect(() => {
    MenuComponent.reinitialization()
  }, [allTransaction])

  const [showModal, setShowModal] = useState<boolean>(false)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [transactionType, setTransactionType] = useState<string>('direct')
  const [showDirectModal, setShowDirectModal] = useState<boolean>(false)
  const [showIndirectModal, setShowIndirectModal] = useState<boolean>(false)

  const componentsRegistry = useMemo(
    () => ({
      companyName: memo(CompanyNameRender),
      fund: memo(FundRender),
      investedRound: memo(InvestedRoundRender),
      amountInvested: memo(AmountInvestedRender),
      valuation: memo(ValuationRender),
      unrealizedValue: memo(UnrealizedValueRender),
      realizedValue: memo(RealizedValueRender),
      unrealizedCost: memo(UnrealizedCostRender),
      FDBPercentage: memo(FdbPercentageRender),
      FDBShares: memo(FdbSharesRender),
      irr: memo(IrrRender),
      moic: memo(MoicRender),
      round: memo(RoundRender),
      esopHolding: memo(EsopHoldingRender),
      jurisdiction: memo(JurisdictionRender),
      tags: memo(TagsRender),
    }),
    []
  )

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      autoHeight: false, // Disable auto height for better performance
      wrapHeaderText: true,
      suppressColumnVirtualisation: false, // Enable column virtualization
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      filterParams: {
        buttons: ['reset'],
        maxNumConditions: 5,
      },
      minWidth: 150,
      filter: true,
      resizable: true,
      flex: 1,
    }),
    []
  )

  function getColumnSetting() {
    if (gridApi.current) return gridApi.current.columnApi?.getColumnState()
    return {}
  }

  // Gets filter model via the grid API
  const getFilterSetting = () => {
    if (gridApi.current) return gridApi.current.api?.getFilterModel()
    return {}
  }

  const [isColumnOpen, setIsColumnOpen] = useState(false)
  const [isFilterOpen, setIsFilterOpen] = useState(false)

  const openToolPanel = (key: any) => {
    if (key === 'columns') {
      if (gridApi) {
        if (!isColumnOpen) gridApi?.current?.api?.openToolPanel(key)
        else gridApi?.current?.api?.closeToolPanel()
        setIsColumnOpen((state) => !state)
        setIsFilterOpen(false)
      }
    } else if (key === 'filters') {
      if (gridApi) {
        if (!isFilterOpen) {
          gridApi?.current?.api?.openToolPanel(key)
        } else gridApi?.current?.api?.closeToolPanel()
        setIsFilterOpen((state) => !state)
        setIsColumnOpen(false)
      }
    }
  }

  const uploadFilterAndColumn = async () => {
    const columnState = await getColumnSetting()
    const filterState = await getFilterSetting()
    // postOnFilter({
    //   userId: `${userId}`,
    //   companyId,
    //   filterData: {
    //     ...agTableStore.state,
    //     transactionColumnModel: columnState,
    //     transactionFilterModel: filterState,
    //   },
    // });
  }

  useEffect(() => {
    if (transactionType === 'direct') {
      setShowDirectModal(true)
      setShowIndirectModal(false)
    } else if (transactionType === 'indirect') {
      setShowIndirectModal(true)
      setShowDirectModal(false)
    }
  }, [transactionType, isModalOpen])

  const [filteredRowData, setFilteredRowData] = useState<any>([])
  const [isFilterApplied, setIsFilterApplied] = useState(false)

  // Debounce filter changes to improve performance
  const debouncedOnAgGridFilterChanged = useMemo(
    () =>
      _.debounce((grid: any) => {
        const filtersApplied = grid.api.isAnyFilterPresent()
        setIsFilterApplied(filtersApplied)

        const filteredData = grid.api.getModel().rowsToDisplay.map((node: any) => node.data)
        setFilteredRowData(filteredData)
        setPinnedBottomRowData(grid)

        uploadFilter()
        uploadFilterAndColumn()
      }, 200),
    [uploadFilter, uploadFilterAndColumn]
  )

  const onAgGridFilterChanged = useCallback(
    (grid: any) => {
      debouncedOnAgGridFilterChanged(grid)
    },
    [debouncedOnAgGridFilterChanged]
  )

  const isExternalFilterPresent = useCallback(() => companySearchTerm.current.length > 0, [])

  const doesExternalFilterPass = useCallback((node: any) => {
    const company = node.data?.companyName
    const searchableCompanyName = [company?.name, company?.dba]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()

    return searchableCompanyName.includes(companySearchTerm.current)
  }, [])

  useEffect(() => {
    const filterModel = gridApi.current?.api.getFilterModel()

    const filteredData = gridApi.current?.api.getModel().rowsToDisplay.map((node: any) => node.data)

    setFilteredRowData(filteredData)
    gridApi.current?.api.setFilterModel(tableState?.filterState)
  }, [gridApi, tableState?.filterState, tableState?.columnState])

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: 'Company',
        field: 'companyName',
        cellRenderer: 'companyName',
        filter: 'agTextColumnFilter',
        filterValueGetter: (params) => {
          const company = params.data?.companyName
          return [company?.dba, company?.name].filter(Boolean).join(' ')
        },
        getQuickFilterText: (params) => {
          return [params.value?.dba, params.value?.name].filter(Boolean).join(' ')
        },
        comparator(valueA, valueB, ..._: any[]) {
          return valueA.name > valueB.name ? 1 : -1
        },

        filterParams: {
          maxNumConditions: 5,
          buttons: ['reset'],
        },
        cellStyle: tableCellStyle,
        // pinned: 'left',
        minWidth: 350,
        sortable: true,
        autoHeight: true,
        wrapText: true,
        suppressSizeToFit: true,
        menuTabs: ['filterMenuTab'],
        tooltipField: 'tooltipTransactionName',
      },
      {
        headerName: 'Fund',
        field: 'fund',
        filter: 'agNumberColumnFilter',
        cellRenderer: 'fund',
        cellStyle: tableCellStyle,
        sortable: true,
        hide: true,
        menuTabs: ['filterMenuTab'],
      },
      // {
      //   headerName: 'Round Invested',
      //   field: 'investedRound',
      //   filter: 'agNumberColumnFilter',
      //   cellRenderer: 'investedRound',
      //   cellStyle: tableCellStyle,
      //   sortable: true,
      //   hide: true,
      //   menuTabs: ['filterMenuTab'],
      // },
      // {
      //   headerName: 'Investment Date',
      //   field: 'dateOfInvestment',
      //   filter: 'agDateColumnFilter',
      //   filterParams: {
      //     comparator: (dateFromFilter: Date, cellValue: any) => {
      //       if (cellValue == null) {
      //         return 0
      //       }
      //       const dateParts = cellValue.split('-')
      //       const day = Number(dateParts[2])
      //       const month = Number(dateParts[1]) - 1
      //       const year = Number(dateParts[0])
      //       const cellDate = new Date(year, month, day)
      //       if (cellDate < dateFromFilter) {
      //         return -1
      //       } else if (cellDate > dateFromFilter) {
      //         return 1
      //       }
      //       return 0
      //     },
      //   },
      //   cellRenderer: 'dateOfInvestment',
      //   cellStyle: tableCellStyle,
      //   sortable: true,
      //   menuTabs: ['filterMenuTab'],
      // },
      {
        headerName: `Amount Invested (${getSymbolOfCurrency(userCurrency)})`,
        field: 'amountInvested',
        filter: 'agNumberColumnFilter',
        cellRenderer: 'amountInvested',
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ['filterMenuTab'],
        tooltipField: 'tooltipAmountInvested',
      },
      {
        headerName: `Unrealized Value (${getSymbolOfCurrency(userCurrency)})`,
        field: 'unrealizedValue',
        filter: 'agNumberColumnFilter',
        cellRenderer: 'unrealizedValue',
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ['filterMenuTab'],
        tooltipField: 'tooltipUnrealizedValue',
      },
      {
        headerName: `Realized Value (${getSymbolOfCurrency(userCurrency)})`,
        field: 'realizedValue',
        filter: 'agNumberColumnFilter',
        cellRenderer: 'realizedValue',
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ['filterMenuTab'],
        tooltipField: 'tooltipRealizedValue',
      },
      // {
      //   headerName: 'Unrealized Cost',
      //   field: 'unrealizedCost',
      //   filter: 'agNumberColumnFilter',
      //   cellRenderer: 'unrealizedCost',
      //   cellStyle: tableCellStyle,
      //   sortable: true,
      //   hide: true,
      //   menuTabs: ['filterMenuTab'],
      // },
      {
        headerName: `Valuation (${getSymbolOfCurrency(userCurrency)})`,
        field: 'valuation',
        filter: 'agNumberColumnFilter',
        cellRenderer: 'valuation',
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ['filterMenuTab'],
        tooltipField: 'tooltipValuation',
      },
      {
        headerName: 'FDB %',
        field: 'FDBPercentage',
        filter: 'agNumberColumnFilter',
        cellRenderer: 'FDBPercentage',
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ['filterMenuTab'],
      },
      {
        headerName: 'FDB Shares',
        field: 'FDBShares',
        filter: 'agNumberColumnFilter',
        cellRenderer: 'FDBShares',
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ['filterMenuTab'],
        tooltipField: 'tooltipFdbShares',
      },
      {
        headerName: 'IRR',
        field: 'irr',
        filter: 'agNumberColumnFilter',
        cellRenderer: 'irr',
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ['filterMenuTab'],
      },
      {
        headerName: 'MOIC',
        field: 'moic',
        filter: 'agNumberColumnFilter',
        cellRenderer: 'moic',
        cellStyle: tableCellStyle,
        sortable: true,
        hide: true,
        menuTabs: ['filterMenuTab'],
      },
      {
        headerName: 'ESOP Holding',
        field: 'esopHolding',
        filter: 'agNumberColumnFilter',
        cellRenderer: 'esopHolding',
        cellStyle: tableCellStyle,
        sortable: true,
        hide: true,
        menuTabs: ['filterMenuTab'],
        tooltipField: 'tooltipEsopHolding',
      },
      {
        headerName: 'No. of Rounds',
        field: 'round',
        filter: 'agNumberColumnFilter',
        cellRenderer: 'round',
        cellStyle: tableCellStyle,
        sortable: true,
        hide: true,
        menuTabs: ['filterMenuTab'],
      },
      {
        headerName: 'Jurisdiction',
        field: 'jurisdiction',
        cellRenderer: 'jurisdiction',
        cellStyle: tableCellStyle,
        sortable: true,
        hide: true,
        menuTabs: ['filterMenuTab'],
      },
      {
        headerName: 'Tags',
        field: 'tags',
        filter: 'agSetColumnFilter',
        cellRenderer: 'tags',
        cellStyle: tableCellStyle,
        sortable: true,
        hide: true,
        menuTabs: ['filterMenuTab'],
      },
      {
        headerName: '',
        field: 'actions',
        pinned: 'right',
        hide: false,
        width: 60,
        maxWidth: 60,
        filter: false,
        menuTabs: [],
        colId: 'action-column',
        suppressNavigable: true,
        suppressColumnsToolPanel: true,
        cellStyle: {
          'padding-left': '9px',
          'padding-right': '4px',
          'padding-top': '5px',
          'line-height': '20px',
        },
        resizable: false,
        sortable: false,
        cellRenderer: ActionRender,
      },
    ],
    [userCurrencyFormat]
  )

  const rowData = useMemo(
    () =>
      allTransaction?.map((company: AllTransactionsType) => {
        const transactions = company.transactionData
        const investorData = company.investorData
        const valuation = toFiniteNumber(company.companyData.valuation)

        // Use more efficient calculation methods
        let totalInvested = 0
        let currentValue = 0
        let realizedValue = 0
        let fdbShares = 0
        let fdbPercentage = 0
        const irrData: { amount: number; date: Date }[] = []

        // Single pass through transactions for better performance
        if (transactions && transactions.length > 0) {
          for (const transaction of transactions) {
            if (
              transaction?.transactionType === Transaction_Type.Buy ||
              transaction?.transactionType === Transaction_Type.SecondaryBuy
            ) {
              const amount = toFiniteNumber(transaction.amount)
              totalInvested += amount
              irrData.push({
                amount: Math.abs(amount) * -1,
                date: new Date(transaction.transactionDate),
              })
            } else if (transaction?.transactionType === Transaction_Type.Sell) {
              const amount = Math.abs(toFiniteNumber(transaction.amount))
              irrData.push({
                amount: amount,
                date: new Date(transaction.transactionDate),
              })
              realizedValue += amount
            }
          }
        }

        // Single pass through investor data
        if (investorData && investorData.length > 0) {
          for (const investor of investorData) {
            currentValue += toFiniteNumber(investor?.currentValue)
            fdbShares += toFiniteNumber(investor?.fdbShares)
            fdbPercentage += toFiniteNumber(investor?.fdbHoldingPercentage)
          }
        }

        // Calculate MOIC
        const moic = totalInvested !== 0 ? (realizedValue + currentValue) / totalInvested : 1

        // Calculate IRR only when needed and cache expensive operations
        let irr: number | null = 0
        if (irrData.length >= 1) {
          irrData.push({ amount: currentValue, date: new Date() })
          try {
            const xirrResult = xirr(irrData)
            if (isNaN(convertRate(xirrResult.rate, RateInterval.Year))) {
              const values = irrData.map((value: any) => value.amount)
              const dates = irrData.map((date: any) => new Date(date.date))
              const irrValue = calculateXirr(values, dates)
              irr = irrValue !== 'NA' ? irrValue * 100 : null
            } else {
              irr = convertRate(xirrResult.rate, RateInterval.Year) * 100
            }
          } catch (error) {
            irr = null
          }
        }

        return {
          companyId: company.companyData.hissaCompanyId || company.companyData.id,
          companyName: {
            name: company.companyData.name,
            dba: company.companyData.dba,
          },
          fund: company.investorData
            ? company.investorData?.map((investor) => investor?.name).join(', ') || '-'
            : '-',
          investedRound: '-',
          isHissaCompany: company.companyData.hissaCompanyId ? true : false,
          amountInvested: totalInvested,
          tooltipAmountInvested: totalInvested.toLocaleString(userCurrencyFormat),
          unrealizedValue: currentValue,
          tooltipUnrealizedValue: currentValue.toLocaleString(userCurrencyFormat),
          realizedValue: realizedValue,
          tooltipRealizedValue: realizedValue.toLocaleString(userCurrencyFormat),
          unrealizedCost: 0,
          FDBPercentage: fdbPercentage * 100.0,
          FDBShares: fdbShares,
          valuation,
          tooltipValuation: valuation.toLocaleString(userCurrencyFormat),
          tooltipFdbShares: fdbShares.toLocaleString(userCurrencyFormat),
          irr: irr,
          moic: moic,
          companyStage: company.companyData.companyStage,
          round: toFiniteNumber(company.companyData.noOfRounds),
          jurisdiction: company.companyData.jurisdiction || 'India',
          tags: company.companyData.tags || [],
          userCurrencyFormat: userCurrencyFormat,
          userCurrency: userCurrency,
          type: company.companyData.type,
          cin: company.companyData.cin,
          id: company.companyData.id,
          esopHolding: toFiniteNumber(company.companyData.esopHolding) * 100.0,
          isIndirect:
            company.indirectTransactionData && company.indirectTransactionData?.length > 0,
          companyDetails: company.companyData,
          allPrivateCompanies: props.allPrivateCompanies,
        }
      }),
    [allTransaction, userCurrencyFormat, userCurrency, props.allPrivateCompanies]
  )

  const buttonOptions = ['Add Transaction', 'Add Captable']

  const icons = [
    <KTSVG
      path='media/icons/duotune/general/gen035.svg'
      className='svg-icon-1 text-gray-700 text-hover-primary'
    />,
    <KTSVG
      path='media/icons/duotune/files/fil009.svg'
      className='svg-icon-1 text-gray-700 text-hover-primary'
    />,
  ]
  const actionMenu = () => {
    let newOptions = buttonOptions
    return newOptions.map((option, index) => {
      return (
        <label
          key={option}
          className='form-check-custom py-3 bg-hover-secondary px-2 text-hover-primary'
          onClick={() => {
            if (option === 'Add Transaction') {
              setShowModal(true)
            } else if (option === 'Add Captable') {
              setShowCaptableModal(true)
            }
          }}
        >
          <span className='form-check-label fs-base fw-normal cursor-pointer fw-bold ms-1'>
            {icons[index]} {option}
          </span>
        </label>
      )
    })
  }

  useEffect(() => {
    if (stageType) {
      setIsFileModal(true)
    }
  }, [stageType, isCaptableModalOpen])

  function setPinnedBottomRowData({ api }: { api: any }) {
    const data = api.rowModel.rootNode
    const filteredData = data.childrenAfterAggFilter.map((element: any) => element.data)
    const sumColumn = (field: string) =>
      filteredData.reduce(
        (accumulator: number, row: any) => accumulator + toFiniteNumber(row[field]),
        0
      )
    const totalInvested = sumColumn('amountInvested')
    const realizedValue = sumColumn('realizedValue')
    const unrealizedValue = sumColumn('unrealizedValue')
    const fdbShares = sumColumn('FDBShares')
    const totalValuation = sumColumn('valuation')

    api.setPinnedBottomRowData([
      {
        companyId: null,
        companyName: {
          name: 'Total',
          dba: null,
        },
        fund: null,
        investedRound: '-',
        amountInvested: totalInvested,
        tooltipAmountInvested: totalInvested.toLocaleString(userCurrencyFormat),
        valuation: totalValuation,
        tooltipValuation: totalValuation.toLocaleString(userCurrencyFormat),
        unrealizedValue: unrealizedValue,
        tooltipUnrealizedValue: unrealizedValue.toLocaleString(userCurrencyFormat),
        realizedValue: realizedValue,
        tooltipRealizedValue: realizedValue.toLocaleString(userCurrencyFormat),
        unrealizedCost: null,
        FDBPercentage: null,
        FDBShares: fdbShares,
        tooltipFdbShares: fdbShares.toLocaleString(userCurrencyFormat),
        irr: null,
        moic: null,
        round: null,
        jurisdiction: null,
        tags: null,
        userCurrencyFormat: userCurrencyFormat,
        userCurrency: userCurrency,
        type: null,
        cin: null,
        id: null,
        esopHolding: null,
        isIndirect: null,
        companyDetails: null,
      },
    ])
  }

  return (
    <div
      className='card p-0 h-800px'
      style={{
        boxShadow: '0px 3px 4px 0px rgba(0, 0, 0, 0.03)',
        border: '1',
        borderColor: '#F1F1F2',
      }}
    >
      <div
        className='d-flex flex-row overflow-auto border-0 p-9'
        style={{ overflowY: 'auto', overflowX: 'auto', scrollbarWidth: 'none' }}
      >
        <div className='card-title'>
          <h1 className='mb-0'>Portfolio Companies</h1>
          <p className='fs-7 text-gray-400'>{allTransaction?.length} Companies</p>
        </div>
        <div className='col d-flex flex-row justify-content-end'>
          {/* <input
            type='search'
            className='form-control h-38px w-auto mw-150-px form-control-solid me-2 fs-base'
            name='search'
            placeholder='Search by Company Name'
            onChange={(e: any) => {
              gridApi.current.api.setQuickFilter(e.target.value)
            }}
          /> */}

          <CloudSetting
            tableId={TableId.portfolioTable + 'web'}
            isLoading={isSaving}
            filterCondition={filterCondition}
            setFilterCondition={setFilterCondition}
          />

          <span
            className={`d-flex flex-column border border-1 border-secondary-txt bg-transparent h-38px items-center w-full rounded`}
            style={{
              boxShadow: '0px 3px 4px 0px rgba(0, 0, 0, 0.03)',
              border: '1',
              borderColor: '#F1F1F2',
              zIndex: 6,
            }}
          >
            <span className='d-flex flex-row'>
              <KTSVG
                path={'media/icons/duotune/general/gen021.svg'}
                className='ps-4 svg-icon-2x text-start fs-9 pt-2'
                svgClassName='text-gray-700'
              />
              <input
                type='search'
                placeholder='Search by Company Name'
                className='form-control w-auto mw-200-px border-0 h-35px rounded'
                onChange={(e: any) => {
                  companySearchTerm.current = e.target.value.trim().toLowerCase()
                  gridApi.current?.api?.onFilterChanged()
                }}
              />
            </span>
          </span>

          {/* <button
            type='button'
            className='px-4 rounded border border-0 border-primary bg-primary h-38px fw-normal ms-4 text-white'
            onClick={() => {
              setShowModal(true)
            }}
          >
            Add Transaction
          </button> */}

          {/* <AddButtonActionRender /> */}
          <div className='px-0'>
            <button
              type='button'
              className='px-3 rounded border border-0 border-primary bg-primary h-38px fw-normal ms-4 text-white dropdown-toggle'
              data-kt-menu-trigger='click'
              data-kt-menu-placement='bottom-end'
              data-kt-menu-flip='top-end'
              data-bs-toggle='dropdown'
            >
              Add Transaction{' '}
            </button>
            <div
              className='menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-800 menu-state-bg-light-primary fw-bold w-150px py-2 mt-1 border border-1 border-gray-400'
              data-kt-menu='true'
              style={{
                boxShadow: '0px 3px 4px 0px rgba(0, 0, 0, 0.03)',
                border: '1',
                borderColor: '#F1F1F2',
              }}
            >
              {actionMenu()}
            </div>
          </div>

          {showModal && (
            <AddTransactionPopup
              transactionType={transactionType}
              showModal={showModal}
              handleClose={() => {
                setShowModal(false)
                setIsModalOpen(false)
              }}
              setTransactionType={(type: string) => setTransactionType(type)}
              isOpen={(isOpen: boolean) => setIsModalOpen(isOpen)}
            />
          )}

          {showCaptableModal && (
            <UploadCaptablePopup
              showModal={showCaptableModal}
              handleClose={() => {
                setShowCaptableModal(false)
                setIsCaptableModalOpen(false)
              }}
              setStageType={(type: string) => setStageType(type)}
              isOpen={(isOpen: boolean) => setIsCaptableModalOpen(isOpen)}
              stageType={stageType}
            />
          )}

          {isFileModal && isCaptableModalOpen && (
            <CaptableFileUploadPopup
              showModal={isFileModal}
              handleClose={() => {
                setIsCaptableModalOpen(false)
                setShowCaptableModal(true)
                setIsFileModal(false)
              }}
              isStageThree={stageType === 'stage_4' ? false : true}
              handleSubmit={() => setShowCaptableModal(false)}
              companyData={{} as CompanyObject}
              companyType={
                stageType === 'stage_4' ? StageCompanyType.Stage_4 : StageCompanyType.Stage_3
              }
            />
          )}

          {showIndirectModal && isModalOpen && (
            <AddIndirectTransactionModal
              showModal={showIndirectModal}
              handleClose={() => {
                setIsModalOpen(false)
                setShowIndirectModal(false)
              }}
              isCompanyExist={false}
              handleSubmit={() => setShowModal(false)}
              isEdit={false}
            />
          )}
          {showDirectModal && isModalOpen && (
            <AddDirectTransactionModal
              showModal={showDirectModal}
              handleClose={() => {
                setIsModalOpen(false)
                setShowDirectModal(false)
              }}
              isCompanyExist={false}
              handleSubmit={() => setShowModal(false)}
              isEdit={false}
            />
          )}

          <button
            type='submit'
            value={'Columns'}
            onClick={() => openToolPanel('columns')}
            className='px-4 rounded border border-1 border-secondary-txt bg-transparent h-38px fw-normal ms-4 text-hover-primary'
          >
            <KTSVG
              path='/media/icons/duotune/layouts/lay004.svg'
              className='svg-icon-3 text-secondary-txt'
            />{' '}
            Columns
          </button>
          <button
            value={'Filters'}
            onClick={() => openToolPanel('filters')}
            className='px-4 rounded border border-1 border-secondary-txt bg-transparent h-38px fw-normal ms-4 text-hover-primary'
          >
            <KTSVG
              path='/media/icons/duotune/general/gen031.svg'
              className='svg-icon-3 text-secondary-txt'
            />{' '}
            Filter
          </button>
        </div>
      </div>
      <div className='card-body w-100 p-9 pt-0 overflow-auto ag-theme-material h-100'>
        <AgGridReact
          sideBar={agConfigSideBar}
          onGridReady={(params) => {
            gridApi.current = params
          }}
          getRowId={(params) => params.data.companyId}
          components={componentsRegistry}
          alwaysShowHorizontalScroll
          alwaysMultiSort
          animateRows={false} // Disable animations for better performance
          defaultColDef={defaultColDef}
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
          onFilterChanged={onAgGridFilterChanged}
          onRowDataUpdated={setPinnedBottomRowData}
          onColumnEverythingChanged={uploadColumn}
          onColumnResized={uploadColumn}
          onColumnMoved={uploadColumn}
          rowData={rowData}
          columnDefs={columnDefs}
          pagination={false}
          enableCharts={true}
          enableRangeSelection={true}
          suppressRowTransform={true}
          suppressCopyRowsToClipboard={true}
          suppressCopySingleCellRanges={true}
          suppressCellFocus={true}
          suppressMenuHide={false}
          tooltipShowDelay={50}
          tooltipInteraction={true}
          rowClass={'border-right-0 border-left-0 border-dashed'}
          // Performance optimizations
          suppressRowVirtualisation={false} // Enable row virtualization
          rowBuffer={10} // Reduce buffer size
          suppressColumnVirtualisation={false} // Enable column virtualization
          maxConcurrentDatasourceRequests={2}
          cacheBlockSize={100} // Optimize cache block size
          maxBlocksInCache={2}
          purgeClosedRowNodes={true}
          suppressAggFuncInHeader={true}
          overlayNoRowsTemplate={
            '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow; margin-top: 50px;">No Rows To Show</span>'
          }
          getRowStyle={(params) => {
            if (params.rowIndex % 2 === 0) {
              return { background: '#f8f8f8' }
            } else {
              return { background: '#ffffff' }
            }
          }}
        ></AgGridReact>
      </div>
    </div>
  )
}
